import React from "react";
import { Box, Container, Typography, Grid, Button, Stack } from "@mui/material";
import { Link } from "react-router-dom";
import HeroSection from "../../components/HeroSection/HeroSection";
import FeaturesSection from "../../components/FeaturesSection/FeaturesSection";
import ScreenshotGallery from "../../components/ScreenshotGallery/ScreenshotGallery";
import EmailSubscription from "../../components/EmailSubscription/EmailSubscription";
import AppDownloadCTA from "../../components/AppDownloadCTA/AppDownloadCTA";
import SEO from "../../components/SEO/SEO";
import { pageMeta } from "../../seo/pageMeta";
import { SITE_URL } from "../../seo/constants";

const learnLinks = [
    {
        to: "/blogs/quran",
        title: "The Holy Quran",
        text: "Understand the Quran — revelation, guidance, and why Muslims recite it daily.",
    },
    {
        to: "/blogs/how-to-pray-in-islam",
        title: "How to Pray Salah",
        text: "Step-by-step guide to Namaz for beginners — from wudu to the five daily prayers.",
    },
    {
        to: "/surah-yaseen",
        title: "Surah Yaseen PDF",
        text: "Download Surah Yasin in Arabic and English for offline recitation and reflection.",
    },
    {
        to: "/quran-pdf-download",
        title: "Quran PDF Download",
        text: "Free Holy Quran PDF in Arabic and English for mobile, tablet, or desktop.",
    },
    {
        to: "/blogs/ramadan",
        title: "Ramadan Guide",
        text: "Fasting, Quran, dua, and spiritual focus during the blessed month of Ramadan.",
    },
    {
        to: "/blogs/five-pillars-of-islam",
        title: "Five Pillars of Islam",
        text: "Shahada, Salah, Zakat, Sawm, and Hajj — the foundation of Muslim practice.",
    },
];

const Home = () => {
    const meta = pageMeta.home;

    return (
        <Box sx={{ minHeight: "calc(100vh - 200px)" }}>
            <SEO
                {...meta}
                jsonLd={{
                    "@context": "https://schema.org",
                    "@type": "WebPage",
                    name: meta.title,
                    description: meta.description,
                    url: `${SITE_URL}/`,
                    primaryImageOfPage: {
                        "@type": "ImageObject",
                        url: `${SITE_URL}/images/screenshots/screen-1.webp`,
                    },
                }}
            />
            <HeroSection />
            <FeaturesSection />
            <ScreenshotGallery />

            <Box
                sx={{
                    py: { xs: 6, md: 10 },
                    background:
                        "linear-gradient(180deg, rgba(240,248,240,0.5) 0%, #ffffff 100%)",
                }}
            >
                <Container maxWidth="lg">
                    <Box sx={{ textAlign: "center", mb: 5 }}>
                        <Typography
                            variant="h2"
                            component="h2"
                            sx={{ mb: 2, fontWeight: 700 }}
                        >
                            Learn Quran, Islam & Deen Online
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                color: "text.secondary",
                                maxWidth: 700,
                                mx: "auto",
                                lineHeight: 1.7,
                            }}
                        >
                            Explore trusted Islamic guides on Allah, Tawhid, Prophet
                            Muhammad ﷺ, dua, Salah, and becoming Muslim — then deepen
                            your practice with the Quran Chat Buddy app.
                        </Typography>
                    </Box>

                    <Grid container spacing={3}>
                        {learnLinks.map((item) => (
                            <Grid item xs={12} sm={6} md={4} key={item.to}>
                                <Box
                                    component={Link}
                                    to={item.to}
                                    sx={{
                                        display: "block",
                                        height: "100%",
                                        p: 3,
                                        textDecoration: "none",
                                        color: "inherit",
                                        borderRadius: 3,
                                        border: "1px solid rgba(46,139,87,0.12)",
                                        background: "#fff",
                                        transition: "all 0.25s ease",
                                        "&:hover": {
                                            borderColor: "primary.main",
                                            boxShadow:
                                                "0 12px 32px rgba(46,139,87,0.12)",
                                            transform: "translateY(-4px)",
                                        },
                                    }}
                                >
                                    <Typography
                                        variant="h6"
                                        component="h3"
                                        sx={{
                                            mb: 1,
                                            fontWeight: 600,
                                            color: "primary.dark",
                                        }}
                                    >
                                        {item.title}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{ color: "text.secondary", lineHeight: 1.65 }}
                                    >
                                        {item.text}
                                    </Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>

                    <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={2}
                        justifyContent="center"
                        sx={{ mt: 5 }}
                    >
                        <Button
                            component={Link}
                            to="/blogs"
                            variant="contained"
                            size="large"
                        >
                            Browse All Islamic Blogs
                        </Button>
                        <Button
                            component={Link}
                            to="/deenbuddy"
                            variant="outlined"
                            size="large"
                            sx={{ borderWidth: 2, "&:hover": { borderWidth: 2 } }}
                        >
                            Explore the App
                        </Button>
                    </Stack>

                    <AppDownloadCTA
                        title="Start your Quran journey today"
                        subtitle="Join Muslims using Quran Chat Buddy for AI Quran answers, Salah prayer times, Qibla, daily ayat, and peaceful Islamic learning — free on iPhone and Android."
                    />
                </Container>
            </Box>

            <EmailSubscription />
        </Box>
    );
};

export default Home;
